import type { ComponentProps, JSX } from 'react'

import { Text, Link } from '@buffer-mono/popcorn'

export type LinkType = {
  url?: string
  displayString: string
  indices: number[]
  className?: string
}

export type OptionType = {
  newTab: boolean
}

type Element = string | JSX.Element

const linkDataToElement = (
  options: OptionType,
  link: LinkType,
  key: number,
): JSX.Element => (
  <Link
    href={link.url ?? ''}
    key={key}
    target={options.newTab ? '_blank' : undefined}
    rel="noopener noreferrer"
    className={link.className}
  >
    {link.displayString}
  </Link>
)

const getLinkifiedText = (
  options: OptionType,
  links: LinkType[],
  text: string,
): Element[] => {
  const elements: Element[] = []
  let currentIndex = 0

  if (!links || links.length === 0) {
    elements.push(text)
    return elements
  }

  links.forEach((link) => {
    const startIndex = link.indices[0]
    const endIndex = link.indices[1]

    if (startIndex < text.length) {
      if (currentIndex !== startIndex) {
        elements.push(text.substring(currentIndex, startIndex))
      }

      elements.push(linkDataToElement(options, link, elements.length))
      currentIndex = endIndex
    }
  })

  if (currentIndex !== text.length) {
    elements.push(text.substring(currentIndex))
  }

  return elements
}

export interface LinkifiedTextProps {
  children?: string | null
  color?: ComponentProps<typeof Text>['color']
  links?: LinkType[]
  newTab?: boolean
  size?:
    | 'extra-small'
    | 'large'
    | 'extra-large'
    | 'mini'
    | 'small'
    | 'sm'
    | 'md'
    | 'lg'
  whitespace?: 'normal' | 'pre' | 'pre-line' | 'pre-wrap' | 'nowrap'
  className?: string
}

const LinkifiedText = ({
  children = '',
  color = 'inherit',
  links = [],
  newTab = true,
  size = 'md',
  whitespace = 'normal',
  className,
}: LinkifiedTextProps): JSX.Element => {
  const options = { newTab }

  const sizeMap = {
    'extra-small': 'sm',
    small: 'sm',
    mini: 'md',
    large: 'lg',
    'extra-large': 'lg',
    sm: 'sm',
    md: 'md',
    lg: 'lg',
  } as const

  return (
    <Text
      translate="no"
      style={{
        whiteSpace: whitespace,
      }}
      size={sizeMap[size]}
      color={color}
      className={className}
    >
      {/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
      {getLinkifiedText(options, links, children as string)}
    </Text>
  )
}

export default LinkifiedText
